import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { RegionService } from 'src/app/services/region.service';
import { DivisionService } from 'src/app/services/division.service';
import { AdministrativeMappingService } from 'src/app/services/administrative-mapping.service';
import { VehicleService } from 'src/app/services/vehicle.service';
import { PaccsbookingService } from 'src/app/services/paccsbooking.service';
import { DepartmentType } from 'src/enum/departmenttype';
import { LookUpResponse } from 'src/app/models/lookup';
import { LotteryService } from 'src/app/services/lottery.service';
import {  of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LotteryReportPopupComponent } from '../lottery-report-popup/lottery-report-popup.component';


@Component({
  selector: 'app-lottery-report',
  templateUrl: './lottery-report.component.html',
  styleUrls: ['./lottery-report.component.scss']
})
export class LotteryReportComponent implements OnInit {

  form: FormGroup;

  financialYearList: LookUpResponse[] = [];
  filterFinancialYearList: LookUpResponse[] = [];

  // Region -> Division -> Sub Division -> Village, cascading exactly like the Run Lottery
  // screen's own geography filters (AdministrativeMappingService + DivisionService), but every
  // level here stays optional ("-- All ... --") since this page reports at whatever geographic
  // granularity the user picks, rather than requiring one exact village to run a draw against.
  regionList: LookUpResponse[] = [];
  filterRegionList: LookUpResponse[] = [];

  divisionList: LookUpResponse[] = [];
  filterDivisionList: LookUpResponse[] = [];

  subDivisionList: LookUpResponse[] = [];
  filterSubDivisionList: LookUpResponse[] = [];

  villageList: LookUpResponse[] = [];
  filterVillageList: LookUpResponse[] = [];

  departmentList: LookUpResponse[] = [];
  filterDepartmentList: LookUpResponse[] = [];

  goList: LookUpResponse[] = [];
  filterGoList: LookUpResponse[] = [];

  schemeList: LookUpResponse[] = [];
  filterSchemeList: LookUpResponse[] = [];

  componentList: LookUpResponse[] = [];
  filterComponentList: LookUpResponse[] = [];

  productList: LookUpResponse[] = [];
  filterProductList: LookUpResponse[] = [];

  // Actual lottery run dates (each row's LotteryDate from /api/lottery/lotterydashboard is
  // the real run date, formatted "dd-MM-yyyy") for the currently selected report scope.
  // Only these dates are enabled in the Lottery Date calendar; every other date is greyed
  // out and unselectable.
  lotteryRunDates: string[] = [];

  // Signature of the scope the enabled dates above were last loaded for; reload is skipped
  // when nothing relevant actually changed (e.g. the auto-selection cascade fires several
  // lookups that resolve to the same scope).
  private lastLoadedRunDateScope: string = '';

  // owlDateTimeFilter predicate - enabled only for the actual lottery run dates. While no
  // dates are loaded (initial paint / no department yet), every date stays enabled so the
  // picker never dead-ends.
  public lotteryDateFilter = (d: Date): boolean => {
    if (!this.lotteryRunDates || this.lotteryRunDates.length === 0) {
      return true;
    }
    return this.lotteryRunDates.indexOf(moment(d).format('DD-MM-YYYY')) >= 0;
  };

  constructor(
    private formBuilder: FormBuilder,
    private regionService: RegionService,
    private divisionService: DivisionService,
    private administrativeMappingService: AdministrativeMappingService,
    private vehicleService: VehicleService,
    private paccsbookingService: PaccsbookingService,
    private lotteryService: LotteryService,
    public tripDialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.getFinancialYear(true);
  }

  initializeForm(): void {
    this.form = this.formBuilder.group({
      financialYearId: [null, Validators.required],
      lotteryDate: [null, Validators.required],
      regionId: [0],
      divisionId: [0],
      subDivisionId: [0],
      villageId: [0],
      departmentId: [null, Validators.required],
      goId: [0],
      schemeId: [0],
      componentId: [0],
      productId: [0]
    });
  }

  getFinancialYear(refresh: boolean): void {
    this.regionService.getFinYear(refresh).subscribe(result => {
      if (!result) {
        return;
      }
      this.financialYearList = result;
      this.filterFinancialYearList = this.financialYearList.slice();

      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;

      let selectedYear: any;
      if (currentMonth >= 4) {
        selectedYear = this.financialYearList.find((x: any) =>
          Number(x.startYear) === currentYear && Number(x.endYear) === currentYear + 1);
      } else {
        selectedYear = this.financialYearList.find((x: any) =>
          Number(x.startYear) === currentYear - 1 && Number(x.endYear) === currentYear);
      }
      if (!selectedYear && this.financialYearList.length > 0) {
        selectedYear = this.financialYearList[this.financialYearList.length - 1];
      }
      if (selectedYear) {
        this.form.controls['financialYearId'].setValue(selectedYear.key);
      }
      this.getDepartments(true);
      this.regionLookup();
    });
  }

  onFinancialYearChange(): void {
    this.departmentList = [];
    this.filterDepartmentList = [];
    this.form.controls['departmentId'].setValue(null);
    this.getDepartments(true);

    this.resetGeoFrom('regionId');
    this.regionLookup();
  }

  private regionLookup(): void {
    const financialYearId = this.form.value.financialYearId ?? 0;
    this.administrativeMappingService.getRegions(financialYearId, true).subscribe(result => {
      this.regionList = result || [];
      this.filterRegionList = this.regionList.slice();

      if (this.filterRegionList.length === 1) {
        this.form.controls['regionId'].setValue(this.filterRegionList[0].key);
        this.onRegionChange();
      }
    });
  }

  onRegionChange(): void {
    this.resetGeoFrom('divisionId');

    const regionId = this.form.value.regionId || 0;
    if (!regionId) {
      return;
    }
    const financialYearId = this.form.value.financialYearId ?? 0;
    this.administrativeMappingService.getDivisions(regionId, financialYearId, true).subscribe(result => {
      this.divisionList = result || [];
      this.filterDivisionList = this.divisionList.slice();

      if (this.filterDivisionList.length === 1) {
        this.form.controls['divisionId'].setValue(this.filterDivisionList[0].key);
        this.onDivisionChange();
      }
    });
  }

  onDivisionChange(): void {
    this.resetGeoFrom('subDivisionId');

    const divisionId = this.form.value.divisionId || 0;
    if (!divisionId) {
      return;
    }
    const financialYearId = this.form.value.financialYearId ?? 0;
    this.administrativeMappingService.getSubDivisions(divisionId, financialYearId, true).subscribe(result => {
      this.subDivisionList = result || [];
      this.filterSubDivisionList = this.subDivisionList.slice();

      if (this.filterSubDivisionList.length === 1) {
        this.form.controls['subDivisionId'].setValue(this.filterSubDivisionList[0].key);
        this.onSubDivisionChange();
      }
    });
  }

  onSubDivisionChange(): void {
    this.resetGeoFrom('villageId');

    const subDivisionId = this.form.value.subDivisionId || 0;
    if (!subDivisionId) {
      return;
    }
    this.divisionService.getvillageListbyId(true, subDivisionId).subscribe(result => {
      this.villageList = result || [];
      this.filterVillageList = this.villageList.slice();

      if (this.filterVillageList.length === 1) {
        this.form.controls['villageId'].setValue(this.filterVillageList[0].key);
      }
    });
  }

  onVillageChange(): void {
  }

  onProductChange(): void {
    this.loadLotteryRunDates();
  }

  // Refetches the set of enabled dates in the Lottery Date picker for the current filters.
  // Reuses the existing /api/lottery/lotterydashboard endpoint (one lightweight call per
  // month of the selected financial year) because its LotteryDate field already carries the
  // actual run date from DBTLotteryRun; a failed month is ignored so a single blip never
  // blanks the whole calendar. Runs whenever anything that changes the scope - department,
  // GO, scheme, component, product - changes.
  // private loadLotteryRunDates(): void {
  //   const financialYearId = this.form.value.financialYearId ?? 0;
  //   const departmentId = this.form.value.departmentId ?? 0;
  //   if (!financialYearId || !departmentId) {
  //     return;
  //   }

  //   const goId = this.form.value.goId || 0;
  //   const schemeId = this.form.value.schemeId || 0;
  //   const componentId = this.form.value.componentId || 0;
  //   const productId = this.form.value.productId || 0;

  //   const signature = [financialYearId, departmentId, goId, schemeId, componentId, productId].join('|');
  //   if (signature === this.lastLoadedRunDateScope) {
  //     return;
  //   }
  //   this.lastLoadedRunDateScope = signature;

  //   const monthCalls = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
  //     .map(month => this.lotteryService
  //       .getDashboardList(financialYearId, departmentId, month, goId, schemeId, componentId, productId, true)
  //       .pipe(catchError(() => of([]))));

  //   forkJoin(monthCalls).subscribe((results: any[][]) => {
  //     const dates: string[] = [];
  //     (results || []).forEach(rows => {
  //       (rows || []).forEach(row => {
  //         if (row && row.lotteryDate && dates.indexOf(row.lotteryDate) < 0) {
  //           dates.push(row.lotteryDate);
  //         }
  //       });
  //     });
  //     this.lotteryRunDates = dates;
  //   });
  // }

  // Refetches the set of enabled dates in the Lottery Date picker for the current filters.
// Reuses the existing /api/lottery/lotterydashboard endpoint (one lightweight call per
// month of the selected financial year) because its LotteryDate field already carries the
// actual run date from DBTLotteryRun; a failed month is ignored so a single blip never
// blanks the whole calendar. Runs whenever anything that changes the scope - department,
// GO, scheme, component, product - changes.
private loadLotteryRunDates(): void {
  const financialYearId = this.form.value.financialYearId ?? 0;
  const departmentId = this.form.value.departmentId ?? 0;
  if (!financialYearId || !departmentId) {
    return;
  }

  const goId = this.form.value.goId || 0;
  const schemeId = this.form.value.schemeId || 0;
  const componentId = this.form.value.componentId || 0;
  const productId = this.form.value.productId || 0;

  const signature = [financialYearId, departmentId, goId, schemeId, componentId, productId].join('|');
  if (signature === this.lastLoadedRunDateScope) {
    return;
  }
  this.lastLoadedRunDateScope = signature;

  this.lotteryService
    .getDashboardList(financialYearId, departmentId, 0, goId, schemeId, componentId, productId, true)
    .pipe(catchError(() => of([])))
    .subscribe((rows: any[]) => {
      const dates: string[] = [];
      (rows || []).forEach(row => {
        if (row && row.lotteryDate && dates.indexOf(row.lotteryDate) < 0) {
          dates.push(row.lotteryDate);
        }
      });
      this.lotteryRunDates = dates;

      this.lotteryDateFilter = (d: Date): boolean => {
        if (!this.lotteryRunDates || this.lotteryRunDates.length === 0) {
          return true;
        }
        return this.lotteryRunDates.indexOf(moment(d).format('DD-MM-YYYY')) >= 0;
      };
    });
}

  private resetGeoFrom(controlName: 'regionId' | 'divisionId' | 'subDivisionId' | 'villageId'): void {
    const order = ['regionId', 'divisionId', 'subDivisionId', 'villageId'];
    const startIndex = order.indexOf(controlName);
    order.slice(startIndex).forEach(name => this.form.controls[name].setValue(0));

    if (startIndex <= 1) { this.divisionList = []; this.filterDivisionList = []; }
    if (startIndex <= 2) { this.subDivisionList = []; this.filterSubDivisionList = []; }
    if (startIndex <= 3) { this.villageList = []; this.filterVillageList = []; }
  }

  onDepartmentChange(): void {
    this.clearGoSchemeComponent();
    this.goLookup();
    this.loadLotteryRunDates();
  }

  private clearGoSchemeComponent(): void {
    this.goList = [];
    this.filterGoList = [];
    this.form.controls['goId'].setValue(0);
    this.clearSchemeComponent();
  }

  private clearSchemeComponent(): void {
    this.schemeList = [];
    this.filterSchemeList = [];
    this.form.controls['schemeId'].setValue(0);
    this.clearComponent();
  }

  private clearComponent(): void {
    this.componentList = [];
    this.filterComponentList = [];
    this.form.controls['componentId'].setValue(0);
    this.clearProduct();
  }

  private clearProduct(): void {
    this.productList = [];
    this.filterProductList = [];
    this.form.controls['productId'].setValue(0);
  }

  private goLookup(): void {
    const financialYearId = this.form.value.financialYearId ?? 0;
    const departmentId = this.form.value.departmentId ?? 0;
    this.paccsbookingService.getGoListbyId(financialYearId, departmentId, true).subscribe(result => {
      this.goList = result || [];
      this.filterGoList = this.goList.slice();

      if (this.filterGoList.length === 1) {
        this.form.controls['goId'].setValue(this.filterGoList[0].key);
        this.onGoChange();
      }
    });
  }

  onGoChange(): void {
    this.clearSchemeComponent();
    this.schemeLookup();
    this.loadLotteryRunDates();
  }

  private schemeLookup(): void {
    const goId = this.form.value.goId ?? 0;
    this.divisionService.getSchemaListbyId(true, goId).subscribe(result => {
      this.schemeList = result || [];
      this.filterSchemeList = this.schemeList.slice();

      if (this.filterSchemeList.length === 1) {
        this.form.controls['schemeId'].setValue(this.filterSchemeList[0].key);
        this.onSchemeChange();
      }
    });
  }

  onSchemeChange(): void {
    this.clearComponent();
    this.componentLookup();
    this.loadLotteryRunDates();
  }

  private componentLookup(): void {
    const goId = this.form.value.goId ?? 0;
    const schemeId = this.form.value.schemeId ?? 0;
    this.divisionService.getComponetListbyId(goId, schemeId, true).subscribe(result => {
      this.componentList = result || [];
      this.filterComponentList = this.componentList.slice();

      if (this.filterComponentList.length === 1) {
        this.form.controls['componentId'].setValue(this.filterComponentList[0].key);
      }
      this.onComponentChange();
    });
  }

  onComponentChange(): void {
    this.clearProduct();
    this.productLookup();
    this.loadLotteryRunDates();
  }

  private productLookup(): void {
    const financialYearId = this.form.value.financialYearId ?? 0;
    const departmentId = this.form.value.departmentId ?? 0;
    const goId = this.form.value.goId ?? 0;
    const schemeId = this.form.value.schemeId ?? 0;
    const componentId = this.form.value.componentId ?? 0;

    if (!goId || !schemeId || !componentId) {
      return;
    }

    this.divisionService.getfilteredProductList(true, financialYearId, departmentId, goId, schemeId, componentId)
      .subscribe(result => {
        this.productList = result || [];
        this.filterProductList = this.productList.slice();
      });
  }

  getDepartments(refresh: boolean): void {
    this.vehicleService.getDepartmentLookupData(refresh).subscribe(result => {
      this.departmentList = result || [];
      this.filterDepartmentList = this.departmentList.slice();

      const smam = this.filterDepartmentList.find(d => d.key === DepartmentType.SMAM);
      if (smam) {
        this.form.controls['departmentId'].setValue(smam.key);
      } else if (this.filterDepartmentList.length === 1) {
        this.form.controls['departmentId'].setValue(this.filterDepartmentList[0].key);
      }

      this.onDepartmentChange();
    });
  }

  private validateFormControl(): void {
    Object.keys(this.form.controls).forEach(field => {
      const control = this.form.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  showReport(): void {
    if (!this.form.valid) {
      this.validateFormControl();
      return;
    }

    // Opens the real lottery-draw data (rank/selected/waiting per run, straight off
    // lotteryService.getLotteryDetails/GetLotteryDetailsAsync) inside a popup - mirroring
    // how every other "Show Report" screen in this app opens results in a MatDialog,
    // just with LotteryReportPopupComponent as the target instead of the RDLC-based
    // ShowDailyReportComponent (there is no RDLC/ReportType for lottery results, and
    // this data already exists in ILotteryRepository).
    //
    // Deliberately NOT the LotteryDrillDownContext shape the dashboard's own
    // navigation chain (dashboard -> division -> subDivision -> village -> beneficiary
    // list -> lottery-details) uses - this screen has no clicked row to anchor on, only
    // this filter form, so it passes plain scalar filter values straight through instead.
    //
    // Note: regionId has no equivalent parameter on getLotteryDetails (it only goes
    // down to division/subDivision/village) - it stays a client-side aid for narrowing
    // the Division dropdown above, same role it already plays for the cascading lookups.
    // departmentName/productName are resolved here (not by the popup/backend) since this
    // component already has the lookup lists loaded for the dropdowns - they're passed
    // straight through into ReportSearchViewModel's DepartmentName/ProductName for
    // the RDLC page header (Month/Department/Product banner). productId of 0 means "all
    // products", which the report shows as "All Products" rather than a resolved name.
    const productId = this.form.value.productId || 0;
    const departmentName = this.filterDepartmentList.find(d => d.key === this.form.value.departmentId)?.value || '';
    const productName = productId
      ? (this.filterProductList.find(p => p.key === productId)?.value || '')
      : 'All Products';

    this.tripDialog.open(LotteryReportPopupComponent, {
      autoFocus: false,
      disableClose: true,
      width: '90%',
      height: '90%',
      panelClass: 'mat-dialog-report',
      data: {
        financialYearId: this.form.value.financialYearId,
        departmentId: this.form.value.departmentId,
        departmentName,
        goId: this.form.value.goId || 0,
        schemeId: this.form.value.schemeId || 0,
        componentId: this.form.value.componentId || 0,
        productId,
        productName,
        divisionId: this.form.value.divisionId || 0,
        subDivisionId: this.form.value.subDivisionId || 0,
        villageId: this.form.value.villageId || 0,
        lotteryDate: moment(this.form.value.lotteryDate).format('YYYY-MM-DD')
      }
    });
  }

  clear(): void {
    this.regionList = [];
    this.filterRegionList = [];
    this.divisionList = [];
    this.filterDivisionList = [];
    this.subDivisionList = [];
    this.filterSubDivisionList = [];
    this.villageList = [];
    this.filterVillageList = [];
    this.departmentList = [];
    this.filterDepartmentList = [];
    this.goList = [];
    this.filterGoList = [];
    this.schemeList = [];
    this.filterSchemeList = [];
    this.componentList = [];
    this.filterComponentList = [];
    this.productList = [];
    this.filterProductList = [];
    this.initializeForm();
    this.getFinancialYear(true);
  }
}
